import type { PitchCondition } from '../types';

/**
 * Per-condition physics multipliers (§5, §17 ConditionsConfig). One enumerated block per
 * DRY/RAIN/MUD. All values and field names are verbatim from §17; a lobby selects one (RANDOM
 * resolves from the seed). NOTE: these TS field names become the canonical-JSON keys serialized on
 * the wire + embedded in the log header + fed to hashState (§17), so they must match §17 exactly and
 * stay identical across server/client/replay (they do — all import these shared types).
 */
export interface ConditionParams {
  readonly KF_ROLL: number; // rolling resistance rate, 1/s (§5.3 — a rate, NOT m/s²)
  readonly REST: number; // ball restitution off the pitch surface (§5.3 REST[surface])
  readonly SLIDE_DECEL_MUL: number; // slide-tackle decel multiplier (§5.4)
  readonly conditionSpeedMul: number; // player movement speed multiplier (§5.2)
  readonly CLAIM_RADIUS_MUL: number; // possession claim radius multiplier (§5.6)
  readonly SPRING_MUL: number; // carry-spring stiffness multiplier (§5.6)
}

export type ConditionsConfig = Readonly<Record<PitchCondition, ConditionParams>>;

export const DEFAULT_CONDITIONS: ConditionsConfig = Object.freeze({
  DRY: Object.freeze({
    KF_ROLL: 0.55,
    REST: 0.72, // D53: livelier ball — bounces read as a real object (was 0.62)
    SLIDE_DECEL_MUL: 1.0,
    conditionSpeedMul: 1.0,
    CLAIM_RADIUS_MUL: 1.0,
    SPRING_MUL: 1.0,
  }),
  RAIN: Object.freeze({
    KF_ROLL: 0.38,
    REST: 0.62, // P22: below DRY — "skiddy" comes from lower rolling friction, not more bounce (D53 lift)
    SLIDE_DECEL_MUL: 0.8,
    conditionSpeedMul: 0.97,
    CLAIM_RADIUS_MUL: 1.0,
    SPRING_MUL: 0.9,
  }),
  MUD: Object.freeze({
    KF_ROLL: 1.1,
    REST: 0.5, // D53 lift (mud still deadens)
    SLIDE_DECEL_MUL: 0.55, // mud slides ~1.8× farther (§5.4, outcome plan §7)
    conditionSpeedMul: 0.88,
    CLAIM_RADIUS_MUL: 1.15,
    SPRING_MUL: 0.75,
  }),
});
